import BloomSpaceBellSvg from '@assets/images/svg/bloom-space-bell.svg';
import BloomSpaceBookSvg from '@assets/images/svg/bloom-space-book.svg';
import BloomSpaceBuildingSvg from '@assets/images/svg/bloom-space-building.svg';
import BloomSpaceCartSvg from '@assets/images/svg/bloom-space-cart.svg';
import BloomSpaceGearSvg from '@assets/images/svg/bloom-space-gear.svg';
import BloomSpaceLeafSvg from '@assets/images/svg/bloom-space-leaf.svg';
import { getColors } from '@/core/constants/uiColors';
import React from 'react';
import { StyleSheet, View } from 'react-native';

const bloomColors = getColors();
import type { BloomSpaceKey } from '../types/bloomUniversalContent';

export interface BloomSpaceTypeIconProps {
  readonly spaceKey: string;
  readonly size?: number;
  readonly color?: string;
  /** Faint circle behind the glyph (hub mosaic). */
  readonly showBadge?: boolean;
  readonly badgeSize?: number;
}

type SvgComponent = React.ComponentType<{
  width?: number;
  height?: number;
  color?: string;
}>;

const SPACE_ICONS: Record<BloomSpaceKey, SvgComponent> = {
  landscape: BloomSpaceLeafSvg,
  properties: BloomSpaceBuildingSvg,
  retail: BloomSpaceCartSvg,
  facilities: BloomSpaceGearSvg,
  hospitality: BloomSpaceBellSvg,
  education: BloomSpaceBookSvg,
};

function isBloomSpaceKey(value: string): value is BloomSpaceKey {
  return value in SPACE_ICONS;
}

/**
 * Hub mosaic top-right icons from Figma exports (not the generic app bell/building).
 */
export function BloomSpaceTypeIcon({
  spaceKey,
  size = 16,
  color = bloomColors.bloomUniversalOnPhoto,
  showBadge = true,
  badgeSize = 36,
}: Readonly<BloomSpaceTypeIconProps>) {
  if (!isBloomSpaceKey(spaceKey)) {
    return null;
  }

  const Icon = SPACE_ICONS[spaceKey];
  const glyph = <Icon width={size} height={size} color={color} />;

  if (!showBadge) {
    return (
      <View style={[styles.glyph, { width: size, height: size }]}>{glyph}</View>
    );
  }

  return (
    <View
      style={[
        styles.badge,
        {
          width: badgeSize,
          height: badgeSize,
          borderRadius: badgeSize / 2,
        },
      ]}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      {glyph}
    </View>
  );
}

const styles = StyleSheet.create({
  badge: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: bloomColors.bloomUniversalIconBadge,
  },
  glyph: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default BloomSpaceTypeIcon;
